/**
 * React hooks for analytics integration.
 * Provides automatic page tracking, scroll depth, and time-on-page measurement.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from './manager';

/**
 * Hook to access analytics tracking methods.
 * Returns stable references that don't change between renders.
 */
export function useAnalytics() {
  const track = useCallback((event, props) => analytics.track(event, props), []);
  const identify = useCallback((userId, traits) => analytics.identify(userId, traits), []);
  const revenue = useCallback((event, amount, currency, props) =>
    analytics.revenue(event, amount, currency, props), []);

  return { track, identify, revenue };
}

/**
 * Auto-tracks page views on route changes.
 * Must be used inside a Router context.
 */
export function usePageTracking() {
  const location = useLocation();
  const prevPath = useRef(null);

  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      analytics.pageView(location.pathname, {
        referrer: prevPath.current || document.referrer,
        search: location.search,
      });
      prevPath.current = location.pathname;
    }
  }, [location.pathname, location.search]);
}

/**
 * Tracks time spent on the current page.
 * Sends timing data via Beacon API on page unload for reliability.
 */
export function useTimeOnPage() {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const currentPath = useRef(location.pathname);

  useEffect(() => {
    startTime.current = Date.now();
    currentPath.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const sendTiming = () => {
      const duration = Date.now() - startTime.current;
      if (duration < 1000) return; // Ignore sub-second visits

      const payload = JSON.stringify({
        event: 'page_time',
        page: currentPath.current,
        duration_ms: duration,
      });

      // Use Beacon API for reliable delivery on page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/v1/analytics/beacon', payload);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendTiming();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', sendTiming);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', sendTiming);
    };
  }, []);
}

/**
 * Tracks scroll depth at 25%, 50%, 75%, and 100% thresholds.
 */
export function useScrollDepth() {
  const location = useLocation();
  const tracked = useRef(new Set());

  useEffect(() => {
    tracked.current = new Set();
  }, [location.pathname]);

  useEffect(() => {
    const thresholds = [25, 50, 75, 100];

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      for (const threshold of thresholds) {
        if (scrollPercent >= threshold && !tracked.current.has(threshold)) {
          tracked.current.add(threshold);
          analytics.track('scroll_depth', {
            page: location.pathname,
            depth: threshold,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
}
